import type { IConfig } from '../../config/iConfig.js';
// Value import (not `import type`): the getClientDataIslands default constructs an empty JsonObject, mirroring
// csharp ILoader.GetClientDataIslands's `=> new()`.
import { JsonObject } from '../../app/json/jsonValue.js';
import type { PhaseTiming } from '../timing/phaseTiming.js';
import type { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import type { InheritanceOperation } from '../../model/inheritanceOperation.js';
import type { NavigationDecision } from '../../model/navigationDecision.js';
import type { ITransientStore } from '../../registry/iface/iTransientStore.js';
import type { AppSiteResult } from '../../registry/iface/appSiteResult.js';
import { IClientReplayLoader } from './iClientReplayLoader.js';

/**
 * ILoader - Common base interface for all loader types
 *
 * This is a marker interface (like C# interfaces) - no method bodies.
 * Method signatures are documented via JSDoc for IDE support.
 * Actual implementations are in the loader classes and their _ILoader.ts partial files.
 *
 * Expected methods (implemented by LoaderNormal, LoaderPreProcess, etc.):
 *
 * Lifecycle:
 * @method load(config, appSite) - Load templates from AppSite
 *
 * Configuration (getters):
 * @property {string} defaultReplaceTemplateName
 * @property {string} defaultSkipTemplateName
 * @property {string} defaultReplaceHtml
 * @property {string} defaultSkipHtml
 * @property {string} defaultSkipJson
 * @property {number} maxArrayOperationItems
 * @property {number} maxRecursionDepth
 *
 * DynamicData:
 * @method getDynamicDataConfig(appSite) - Get dynamic data config
 * @method getDynamicActionConfig(appSite) - Get dynamic action config
 * @method setActionMode(actionMode) - Enable/disable action mode
 * @method getComponentId(appSite, componentName) - Get ComponentID from DynamicView config
 *
 * GlobalData:
 * @method getGlobalData(appSite) - Get global data for the specified AppSite
 * @method getGlobalViewData(appSite) - Get global view data mapping for the specified AppSite
 * @method getGlobalViewDataForComponent(appSite, componentName) - Get resolved global view data for a specific component
 *
 * Inheritance:
 * @method getAndClearInheritanceOperations() - Get and clear inheritance ops
 *
 * Helper:
 * @method getBaseComponentName(name, templateName) - Extract base component name
 */
export abstract class ILoader extends IClientReplayLoader {
    /** @param timing Records the load phases (source, parse, prepare, dynamic) */
    abstract load(config: IConfig, appSite: string, timing: PhaseTiming): boolean;

    abstract get defaultReplaceTemplateName(): string;
    abstract get defaultSkipTemplateName(): string;
    /**
     * AppView placeholder prefix (e.g. "Main") replaced case-insensitively with the active
     * AppView name to derive the canonical AppView content template name (e.g. "MainContent"
     * + appView "ViewA" -> "ViewAContent"). Engines use this for operation-tracking name parity.
     */
    abstract get defaultAppViewPrefix(): string;
    abstract get defaultReplaceHtml(): string;
    abstract get defaultSkipHtml(): string;
    abstract get defaultSkipJson(): string;
    abstract get maxArrayOperationItems(): number;
    abstract get maxRecursionDepth(): number;
    abstract get maxMergeIterations(): number;

    abstract getDynamicDataConfig(appSite: string): DynamicConfigMap | null;
    abstract getDynamicDataParams(appSite: string, paramFile: string): JsonObject | null;

    // #region DynamicClientData

    /**
     * Gets the CLIENT-DATA preparer binding configuration for the specified AppSite (dynamicclientdata.json).
     * Default no-op (null) so loaders/engines without client-data support compile unchanged — only the
     * Normal loader wires it in P2A (the Loader ScenarioFilter reference).
     */
    getDynamicClientDataConfig(appSite: string): DynamicConfigMap | null {
        void appSite;
        return null;
    }

    /**
     * Gets client-data preparer parameters for the specified AppSite and param file. Default no-op (null).
     */
    getDynamicClientDataParams(appSite: string, paramFile: string): JsonObject | null {
        void appSite; void paramFile;
        return null;
    }

    /**
     * P3A ON-DEMAND slice: resolves the dynamicclientdata.json binding for the requested handler and returns
     * the raw producer output via the SAME context-light producer the merge uses
     * (RegistryStore.invokeDynamicClientData) — one producer, two seams. Does NOT run a page merge; callerParams
     * is handed straight in (paging keys). Returns null when the AppSite declares no binding for the handler —
     * the /api/handler-data endpoint maps that to a 400 (no arbitrary handler probing). Default no-op (null).
     *
     * Not gated on DataSource ⊇ Remote: that flag says WHERE the island is delivered at render time (Embedded =
     * with the server's own render), not who is allowed to ask for it. A client rendering the AppSite itself
     * needs exactly the data the Embedded island would have carried, so gating here would force every Embedded
     * AppSite to redeclare itself merely to be client-mergeable. The declaration in dynamicclientdata.json
     * remains the bound: an undeclared handler is still unreachable.
     */
    getClientDataSlice(appSite: string, handlerName: string, callerParams: JsonObject): JsonObject | null {
        void appSite; void handlerName; void callerParams;
        return null;
    }

    /**
     * Returns EVERY client-data island the AppSite declares, keyed "<component>_<IType>" — the same shape as
     * MergeResult.dynamicClientData and ConfigClient.dynamicClientData, so a caller can hand the result straight
     * to a client merge as its delivered island set.
     *
     * Exists for the one case the Embedded island cannot cover: a browser merging an AppSite that was never
     * server-rendered on the page. The Embedded island travels with the server's render of an AppSite, so a page
     * that renders AppSite A holds A's islands — a ClientJs merge of AppSite B on that page has none, and
     * get-templates deliberately carries templates only. The client fetches B's set here instead.
     *
     * Deliberately NOT gated on Remote, for the same reason getClientDataSlice is not. DataSource declares
     * WHERE an island is delivered at render time (Embedded = alongside the server-rendered page); it does not
     * say who may render.
     * A client performing the render itself needs the same data an Embedded island would have carried, so gating
     * this on Remote would only force every Embedded AppSite to redeclare itself to be client-mergeable. Still
     * bounded to the bindings the AppSite declares in dynamicclientdata.json — an undeclared handler stays
     * unreachable, so this opens no arbitrary handler probing. Default no-op (empty).
     */
    getClientDataIslands(appSite: string, callerParams: JsonObject): JsonObject {
        void appSite; void callerParams;
        return new JsonObject();
    }

    /**
     * True when the AppSite declares at least one client-data binding whose producer is registered — i.e. when
     * getClientDataIslands could return a non-empty set. Applies the SAME predicate as that method, so the answer
     * can never disagree with the set it describes.
     *
     * Reported by /api/get-templates so a client merge knows whether an island fetch is worth a round trip: most
     * AppSites declare no client data at all, and asking for their (always empty) island set would add a second
     * request to every client merge for nothing. Cheap by construction — resolves the declaration only and never
     * runs a producer, which is what makes it usable as a pre-check.
     */
    hasClientData(appSite: string): boolean {
        void appSite;
        return false;
    }

    // #endregion

    abstract getDynamicViewConfig(appSite: string): DynamicConfigMap | null;
    abstract getDynamicViewParams(appSite: string, paramFile: string): JsonObject | null;

    abstract getDynamicActionConfig(appSite: string): DynamicConfigMap | null;
    abstract getDynamicActionParams(appSite: string, paramFile: string): JsonObject | null;

    // #region DynamicAppSite

    /**
     * Resolves the AppSite/AppView to render by running any AppSite-triggered IAppSite handlers
     * configured for the requested AppSite (dynamicappsite.json, keyed by AppSite name). Mirrors
     * applyDynamicDataHandlers ownership: the loader owns config lookup, provider dispatch, and reload
     * of any redefined target. Returns the requested AppSite/AppView unchanged when no handler applies.
     */
    abstract applyDynamicAppSiteHandlers(appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming): AppSiteResult;

    /**
     * Ensures the given AppSite's templates/data are loaded (using the loader's retained config), so an
     * IAppSite handler that redefines the AppSite at merge entry can render a target AppSite the loader
     * was not originally loaded for. Idempotent (no-op when already loaded).
     */
    abstract ensureAppSiteLoaded(appSite: string, timing: PhaseTiming): boolean;

    /**
     * Gets dynamic AppSite handler configuration for the specified AppSite.
     */
    abstract getDynamicAppSiteConfig(appSite: string): DynamicConfigMap | null;

    /**
     * Gets dynamic AppSite handler parameters for the specified AppSite and param file.
     */
    abstract getDynamicAppSiteParams(appSite: string, paramFile: string): JsonObject | null;

    // #endregion

    abstract setActionMode(actionMode: boolean): void;

    abstract getComponentId(appSite: string, componentName: string): string | null;
    abstract getGlobalData(appSite: string): JsonObject | null;
    abstract getGlobalViewData(appSite: string): JsonObject | null;
    abstract getGlobalViewDataForComponent(appSite: string, componentName: string): JsonObject | null;
    abstract getAndClearInheritanceOperations(): InheritanceOperation[];
    abstract getAndClearNavigationTrace(): NavigationDecision[];
    abstract setTransientStore(transientStore: ITransientStore): void;
    abstract getTransientStore(): ITransientStore;
    abstract getBaseComponentName(name: string, templateName: string): { baseComponentName: string; isReuseComponent: boolean };
}
